/**
 * ROI calculator: per industry configuration.
 *
 * INTERNAL MAINTAINER NOTE (never shown verbatim to site visitors).
 * Revisit every 6 to 12 months. Several inputs come from fast moving 2025 to 2026
 * industry reports that are refreshed annually.
 *
 * Named source categories used for the defaults below:
 *  - NHS England and British Journal of General Practice (UK: outpatient and GP DNA reporting)
 *  - Cochrane Database of Systematic Reviews (global: SMS and phone reminder effectiveness)
 *  - CallRail (US and UK: missed call rate by industry, analysis of over 1 million leads)
 *  - Federation of Small Businesses (UK: tradesperson missed call and response time research)
 *  - Kerfuffle and Moneypenny, 2022 (UK: estate and letting agency missed call cost study)
 *  - Harvard Business Review (global: 2,241 company lead response and qualification study)
 *  - Velocify "Ultimate Contact Strategy" (US: 3.5 million lead contact and conversion analysis)
 *  - Drift Lead Response Survey (global B2B: 433 company real form submission study)
 *  - Peerlogic (US: dental call tracking across 26 practices, 4,280 calls)
 *  - UK private clinic no show benchmarking (physiotherapy, aesthetics, podiatry, osteopathy)
 *  - UK hotel group call tracking case data (after hours reservation recovery)
 *  - Forrester, Comm100, Freshworks (global: live chat and cart abandonment research)
 *  - Salesforce State of the Connected Customer, McKinsey Global Logistics Survey
 *
 * RIGOUR NOTE: the widely repeated claims "78% of customers buy from the first business
 * that responds" and "35 to 50% of deals go to the first responder" have no traceable
 * primary study and are frequently misattributed. They are deliberately excluded.
 *
 * PROXY ENTRIES: insurance (missed call rate only), recruitment, manufacturing, logistics,
 * IT and operational enterprises, and education have no dedicated published missed call or
 * conversion study in any country at the time of writing. They use the closest well evidenced
 * parallel sector and are flagged as estimates in the sources panel.
 *
 * AI capture rate (90%) is Clinic Genius AI live deployment data, a product performance
 * figure, not third party research. It is labelled as such in the sources panel.
 */

export type SecondaryKind = "noshow" | "abandonment" | "speed";

export interface IndustryConfig {
  id: string;
  name: string;
  /** Word used for the end customer, e.g. patients, guests, candidates. */
  noun: string;
  nounSingular: string;
  /** Word used for the inbound unit, e.g. calls, enquiries. */
  enquiryLabel: string;
  volumeLabel: string;
  missedLabel: string;
  valueLabel: string;
  valuePlaceholder: number;
  transactionLabel: string;
  /** Defaults */
  volume: number;
  missedRate: number;
  missedRange: [number, number];
  conversion: number;
  conversionRange: [number, number];
  secondary: SecondaryKind;
  secondaryLabel: string;
  secondaryRate: number;
  secondaryRange: [number, number];
  /** Proportion of the secondary leakage Astreon systems remove. */
  secondaryReduction: number;
  secondaryReductionRange: [number, number];
  transactionValue: number;
  adminHours: number;
  /** Indicative annual system cost, easy to update per industry tier. */
  annualCost: number;
  /** Confidence in the evidence base for this industry. */
  evidence: "strong" | "proxy";
  /** Plain English assumption lines shown in the sources panel. */
  sources: string[];
}

/** Clinic Genius AI live deployment data, not third party research. */
export const AI_CAPTURE_RATE = 0.9;

const speedLine =
  "Speed of response is the strongest lever we can pull. Harvard Business Review's study of 2,241 companies found that responding within an hour makes a business roughly seven times more likely to qualify a web generated lead than responding an hour later. Velocify's analysis of about 3.5 million leads found calling within one minute lifts conversion by 391%.";

const proxyLine =
  "We use a conservative estimate based on comparable service sectors globally, since a dedicated study for this exact industry was not available.";

export const INDUSTRIES: IndustryConfig[] = [
  {
    id: "healthcare",
    name: "Healthcare",
    noun: "patients",
    nounSingular: "patient",
    enquiryLabel: "calls",
    volumeLabel: "Incoming patient calls per month",
    missedLabel: "Calls missed or unanswered",
    valueLabel: "Average lifetime value of a new patient",
    valuePlaceholder: 850,
    transactionLabel: "Average appointment value",
    volume: 600,
    missedRate: 25,
    missedRange: [20, 35],
    conversion: 45,
    conversionRange: [35, 65],
    secondary: "noshow",
    secondaryLabel: "No show or DNA rate",
    secondaryRate: 12,
    secondaryRange: [5, 25],
    secondaryReduction: 30,
    secondaryReductionRange: [15, 50],
    transactionValue: 120,
    adminHours: 18,
    annualCost: 14400,
    evidence: "strong",
    sources: [
      "UK dental practices typically miss 20 to 30% of incoming calls, and it is worse over lunch and at peak times. Broader multi country phone studies found 32 to 38% unanswered during business hours. We use a conservative 25% by default.",
      "Answered and well handled calls convert to a booked appointment at 40 to 70%, with strong practices near 75% and the industry average closer to 53%. We use 45%.",
      "NHS outpatient DNA sits near 7.6% and GP appointments near 5 to 9.3%. UK private clinic no shows vary by specialty: physiotherapy 6 to 12%, osteopathy and chiropractic 7 to 13%, podiatry 8 to 15%, aesthetics 10 to 18%.",
      "Reminder research, including a Cochrane review of SMS and phone reminders, confirms reminders reliably improve attendance. Clinics combining reminders, deposits and smart scheduling report 50 to 65% reductions, reminders alone closer to 10 to 30%. We use 30%.",
      "The 90% capture rate is based on Clinic Genius AI live deployment data, not external research.",
    ],
  },
  {
    id: "law-firms",
    name: "Law firms",
    noun: "clients",
    nounSingular: "client",
    enquiryLabel: "enquiries",
    volumeLabel: "Incoming client enquiries per month",
    missedLabel: "Enquiries missed or unanswered",
    valueLabel: "Average value of a new client matter",
    valuePlaceholder: 2400,
    transactionLabel: "Average matter value",
    volume: 220,
    missedRate: 28,
    missedRange: [25, 35],
    conversion: 45,
    conversionRange: [30, 60],
    secondary: "speed",
    secondaryLabel: "Enquiries not answered inside one hour",
    secondaryRate: 45,
    secondaryRange: [10, 80],
    secondaryReduction: 35,
    secondaryReductionRange: [15, 50],
    transactionValue: 2400,
    adminHours: 12,
    annualCost: 16800,
    evidence: "strong",
    sources: [
      "Missed enquiry rates of 25 to 35% are consistent with CallRail's analysis of over one million leads across a US and UK client base. We use 28%.",
      speedLine,
    ],
  },
  {
    id: "real-estate",
    name: "Real estate",
    noun: "applicants",
    nounSingular: "applicant",
    enquiryLabel: "calls",
    volumeLabel: "Incoming enquiries per branch per month",
    missedLabel: "Enquiries missed or unanswered",
    valueLabel: "Average commission or fee per completed deal",
    valuePlaceholder: 3200,
    transactionLabel: "Average fee per instruction",
    volume: 320,
    missedRate: 12,
    missedRange: [5, 25],
    conversion: 25,
    conversionRange: [10, 40],
    secondary: "speed",
    secondaryLabel: "Enquiries not answered inside one hour",
    secondaryRate: 40,
    secondaryRange: [10, 80],
    secondaryReduction: 35,
    secondaryReductionRange: [15, 50],
    transactionValue: 3200,
    adminHours: 14,
    annualCost: 15600,
    evidence: "strong",
    sources: [
      "UK research puts branches at roughly 5 to 10 missed calls a week, and 85% of callers who do not get through never try again. UK estate and letting agents lose an estimated £119 million a year to missed calls.",
      "Internet lead to close sits at 2 to 4% in US market data, while a phone enquiry converting to a viewing is far higher. We use 25% for enquiry to viewing.",
      speedLine,
    ],
  },
  {
    id: "insurance",
    name: "Insurance",
    noun: "policyholders",
    nounSingular: "policyholder",
    enquiryLabel: "enquiries",
    volumeLabel: "Incoming quote enquiries per month",
    missedLabel: "Enquiries missed or unanswered",
    valueLabel: "Average value of a new policy",
    valuePlaceholder: 620,
    transactionLabel: "Average premium value",
    volume: 500,
    missedRate: 15,
    missedRange: [5, 30],
    conversion: 35,
    conversionRange: [20, 55],
    secondary: "speed",
    secondaryLabel: "Quotes not returned inside one hour",
    secondaryRate: 50,
    secondaryRange: [10, 85],
    secondaryReduction: 35,
    secondaryReductionRange: [15, 50],
    transactionValue: 620,
    adminHours: 15,
    annualCost: 15600,
    evidence: "proxy",
    sources: [
      `Insurance is a quote comparison category where speed reliably wins. On the missed enquiry rate specifically, ${proxyLine.charAt(0).toLowerCase() + proxyLine.slice(1)}`,
      speedLine,
    ],
  },
  {
    id: "finance",
    name: "Finance",
    noun: "clients",
    nounSingular: "client",
    enquiryLabel: "enquiries",
    volumeLabel: "Incoming client enquiries per month",
    missedLabel: "Enquiries missed or unanswered",
    valueLabel: "Average value of a new client relationship",
    valuePlaceholder: 1800,
    transactionLabel: "Average consultation or product value",
    volume: 280,
    missedRate: 18,
    missedRange: [5, 35],
    conversion: 35,
    conversionRange: [20, 55],
    secondary: "noshow",
    secondaryLabel: "Consultation no show rate",
    secondaryRate: 10,
    secondaryRange: [3, 25],
    secondaryReduction: 30,
    secondaryReductionRange: [15, 50],
    transactionValue: 450,
    adminHours: 12,
    annualCost: 16800,
    evidence: "proxy",
    sources: [
      "Average lead response time in financial services is around eight hours, among the faster considered purchase sectors, so even modest improvements are measurable.",
      speedLine,
    ],
  },
  {
    id: "hospitality",
    name: "Hospitality",
    noun: "guests",
    nounSingular: "guest",
    enquiryLabel: "calls",
    volumeLabel: "Reservation enquiries per month",
    missedLabel: "Enquiries missed or unanswered",
    valueLabel: "Average value of a new guest booking",
    valuePlaceholder: 420,
    transactionLabel: "Average booking value",
    volume: 900,
    missedRate: 30,
    missedRange: [20, 50],
    conversion: 30,
    conversionRange: [25, 35],
    secondary: "noshow",
    secondaryLabel: "Cancellation or no show rate",
    secondaryRate: 12,
    secondaryRange: [5, 25],
    secondaryReduction: 30,
    secondaryReductionRange: [15, 50],
    transactionValue: 420,
    adminHours: 20,
    annualCost: 15600,
    evidence: "strong",
    sources: [
      "Roughly 31% of reservation enquiries arrive after hours and are largely lost to voicemail. Peak period abandonment commonly runs 30 to 50%. We use 30%.",
      "A 14 property UK hotel group cut peak abandonment from 26% to 4% with AI coverage, recovering roughly £1.9 million annually.",
      "Around 25 to 35% of answered reservation calls convert to a booking. We use 30%.",
    ],
  },
  {
    id: "travel-tourism",
    name: "Travel and tourism",
    noun: "travellers",
    nounSingular: "traveller",
    enquiryLabel: "enquiries",
    volumeLabel: "Booking enquiries per month",
    missedLabel: "Enquiries missed or unanswered",
    valueLabel: "Average value of a new booking",
    valuePlaceholder: 980,
    transactionLabel: "Average trip value",
    volume: 600,
    missedRate: 30,
    missedRange: [20, 50],
    conversion: 30,
    conversionRange: [20, 45],
    secondary: "noshow",
    secondaryLabel: "Cancellation or no show rate",
    secondaryRate: 10,
    secondaryRange: [3, 25],
    secondaryReduction: 30,
    secondaryReductionRange: [15, 50],
    transactionValue: 980,
    adminHours: 16,
    annualCost: 15600,
    evidence: "proxy",
    sources: [
      "Travel shows the same after hours booking leakage pattern as hospitality, seen consistently across US and European hotel and vacation rental data. We apply the hospitality research as a proxy.",
      proxyLine,
    ],
  },
  {
    id: "ecommerce",
    name: "E-commerce",
    noun: "customers",
    nounSingular: "customer",
    enquiryLabel: "chat enquiries",
    volumeLabel: "Chat and message enquiries per month",
    missedLabel: "Enquiries abandoned without a fast reply",
    valueLabel: "Average value of a new customer",
    valuePlaceholder: 95,
    transactionLabel: "Average order value",
    volume: 2400,
    missedRate: 50,
    missedRange: [25, 70],
    conversion: 20,
    conversionRange: [10, 35],
    secondary: "abandonment",
    secondaryLabel: "Cart abandonment rate",
    secondaryRate: 68,
    secondaryRange: [40, 85],
    secondaryReduction: 12,
    secondaryReductionRange: [5, 25],
    transactionValue: 95,
    adminHours: 14,
    annualCost: 13200,
    evidence: "strong",
    sources: [
      "Forrester's consumer research found 53% of online shoppers abandon an intended purchase if they cannot get a quick answer to a question.",
      "Comm100 data shows around 60% of shoppers abandon a chat entirely when there is no reply inside 60 seconds, and replying inside that window lifts conversion by roughly 20%.",
    ],
  },
  {
    id: "retail",
    name: "Retail",
    noun: "customers",
    nounSingular: "customer",
    enquiryLabel: "calls",
    volumeLabel: "Store enquiries and calls per month",
    missedLabel: "Enquiries missed or unanswered",
    valueLabel: "Average value of a new customer",
    valuePlaceholder: 140,
    transactionLabel: "Average basket value",
    volume: 800,
    missedRate: 25,
    missedRange: [10, 45],
    conversion: 20,
    conversionRange: [10, 35],
    secondary: "abandonment",
    secondaryLabel: "Enquiry abandonment rate",
    secondaryRate: 55,
    secondaryRange: [25, 80],
    secondaryReduction: 12,
    secondaryReductionRange: [5, 25],
    transactionValue: 140,
    adminHours: 12,
    annualCost: 13200,
    evidence: "proxy",
    sources: [
      "Live chat and enquiry response inside 60 seconds materially reduces abandonment. The evidence base is e-commerce specific and applied here as a reasonable retail proxy.",
      proxyLine,
    ],
  },
  {
    id: "recruitment",
    name: "Recruitment",
    noun: "candidates",
    nounSingular: "candidate",
    enquiryLabel: "enquiries",
    volumeLabel: "Candidate and client enquiries per month",
    missedLabel: "Enquiries missed or unanswered",
    valueLabel: "Average placement fee",
    valuePlaceholder: 4200,
    transactionLabel: "Average placement fee",
    volume: 400,
    missedRate: 20,
    missedRange: [5, 40],
    conversion: 35,
    conversionRange: [15, 55],
    secondary: "speed",
    secondaryLabel: "Enquiries not answered inside one hour",
    secondaryRate: 45,
    secondaryRange: [10, 85],
    secondaryReduction: 35,
    secondaryReductionRange: [15, 50],
    transactionValue: 4200,
    adminHours: 16,
    annualCost: 16800,
    evidence: "proxy",
    sources: [
      "Candidate and client response speed strongly predicts placement outcomes and follows the same decay curve documented across insurance, real estate and B2B sales globally.",
      proxyLine,
      speedLine,
    ],
  },
  {
    id: "construction",
    name: "Construction",
    noun: "clients",
    nounSingular: "client",
    enquiryLabel: "calls",
    volumeLabel: "Incoming job enquiries per month",
    missedLabel: "Calls missed while on site",
    valueLabel: "Average value of a new job",
    valuePlaceholder: 3800,
    transactionLabel: "Average job value",
    volume: 180,
    missedRate: 30,
    missedRange: [20, 60],
    conversion: 30,
    conversionRange: [15, 50],
    secondary: "speed",
    secondaryLabel: "Enquiries not answered inside one hour",
    secondaryRate: 50,
    secondaryRange: [10, 85],
    secondaryReduction: 35,
    secondaryReductionRange: [15, 50],
    transactionValue: 3800,
    adminHours: 10,
    annualCost: 12000,
    evidence: "strong",
    sources: [
      "Federation of Small Businesses research puts collective losses to UK tradespeople at roughly £24,000 a year per business from missed calls. Comparable US home services research finds 40 to 70% of calls missed when crews are on site. We use a conservative 30%.",
      speedLine,
    ],
  },
  {
    id: "manufacturing",
    name: "Manufacturing",
    noun: "buyers",
    nounSingular: "buyer",
    enquiryLabel: "enquiries",
    volumeLabel: "Inbound enquiries and RFQs per month",
    missedLabel: "Enquiries missed or unanswered",
    valueLabel: "Average value of a new account",
    valuePlaceholder: 9500,
    transactionLabel: "Average order value",
    volume: 140,
    missedRate: 20,
    missedRange: [5, 40],
    conversion: 25,
    conversionRange: [10, 45],
    secondary: "speed",
    secondaryLabel: "Enquiries not answered inside one hour",
    secondaryRate: 60,
    secondaryRange: [20, 90],
    secondaryReduction: 35,
    secondaryReductionRange: [15, 50],
    transactionValue: 9500,
    adminHours: 12,
    annualCost: 18000,
    evidence: "proxy",
    sources: [
      "B2B lead response in manufacturing averages roughly 42 hours, the slowest sector benchmarked in Drift's 433 company study, so the room for improvement is large.",
      proxyLine,
      speedLine,
    ],
  },
  {
    id: "logistics",
    name: "Logistics",
    noun: "clients",
    nounSingular: "client",
    enquiryLabel: "enquiries",
    volumeLabel: "Inbound enquiries per month",
    missedLabel: "Enquiries missed or unanswered",
    valueLabel: "Average value of a new account",
    valuePlaceholder: 7200,
    transactionLabel: "Average contract value",
    volume: 200,
    missedRate: 20,
    missedRange: [5, 40],
    conversion: 25,
    conversionRange: [10, 45],
    secondary: "speed",
    secondaryLabel: "Enquiries not answered inside one hour",
    secondaryRate: 55,
    secondaryRange: [20, 90],
    secondaryReduction: 35,
    secondaryReductionRange: [15, 50],
    transactionValue: 7200,
    adminHours: 14,
    annualCost: 18000,
    evidence: "proxy",
    sources: [
      "Logistics shows the same slow B2B response pattern as manufacturing. McKinsey's global logistics research separately finds digital first leaders achieve 10 to 20% short term performance gains from technology adoption.",
      proxyLine,
      speedLine,
    ],
  },
  {
    id: "it-operations",
    name: "IT and operational enterprises",
    noun: "clients",
    nounSingular: "client",
    enquiryLabel: "enquiries",
    volumeLabel: "Inbound enquiries and demo requests per month",
    missedLabel: "Enquiries missed or unanswered",
    valueLabel: "Average value of a new account",
    valuePlaceholder: 6400,
    transactionLabel: "Average contract value",
    volume: 260,
    missedRate: 15,
    missedRange: [5, 35],
    conversion: 30,
    conversionRange: [10, 50],
    secondary: "speed",
    secondaryLabel: "Enquiries not answered inside one hour",
    secondaryRate: 55,
    secondaryRange: [20, 90],
    secondaryReduction: 35,
    secondaryReductionRange: [15, 50],
    transactionValue: 6400,
    adminHours: 14,
    annualCost: 18000,
    evidence: "proxy",
    sources: [
      "B2B software and SaaS average lead response time sits near 12 hours on aggregated industry benchmarks, and Salesforce research finds 82% of B2B buyers expect an immediate response to sales enquiries.",
      proxyLine,
      speedLine,
    ],
  },
  {
    id: "education",
    name: "Education",
    noun: "students",
    nounSingular: "student",
    enquiryLabel: "enquiries",
    volumeLabel: "Admissions enquiries per month",
    missedLabel: "Enquiries missed or unanswered",
    valueLabel: "Average value of a new enrolment",
    valuePlaceholder: 5200,
    transactionLabel: "Average course or term value",
    volume: 300,
    missedRate: 20,
    missedRange: [5, 40],
    conversion: 35,
    conversionRange: [15, 55],
    secondary: "noshow",
    secondaryLabel: "Open day or interview no show rate",
    secondaryRate: 15,
    secondaryRange: [5, 30],
    secondaryReduction: 30,
    secondaryReductionRange: [15, 50],
    transactionValue: 5200,
    adminHours: 16,
    annualCost: 15600,
    evidence: "proxy",
    sources: [
      "Admissions enquiry response follows the same considered purchase decay pattern seen in healthcare, legal and financial services globally.",
      proxyLine,
    ],
  },
];

export const getIndustry = (id: string) =>
  INDUSTRIES.find((i) => i.id === id) ?? INDUSTRIES[0];

export interface CalcInputs {
  volume: number;
  missedRate: number;
  conversion: number;
  newCustomerValue: number;
  secondaryRate: number;
  secondaryReduction: number;
  transactionValue: number;
  adminHours: number;
}

export interface CalcResult {
  recoveredEnquiries: number;
  newCustomers: number;
  primaryRevenue: number;
  secondaryRevenue: number;
  monthlyTotal: number;
  annualTotal: number;
  monthlyLoss: number;
  roiMultiple: number;
  paybackLabel: string;
  hoursReclaimed: number;
  monthlyCost: number;
}

export function calculate(cfg: IndustryConfig, i: CalcInputs): CalcResult {
  const recoveredEnquiries = i.volume * (i.missedRate / 100) * AI_CAPTURE_RATE;
  const newCustomers = recoveredEnquiries * (i.conversion / 100);
  const primaryRevenue = newCustomers * i.newCustomerValue;

  // Secondary leakage: share of monthly volume leaking, share of that leak we remove,
  // valued at the relevant transaction value.
  const secondaryRevenue =
    i.volume *
    (i.secondaryRate / 100) *
    (i.secondaryReduction / 100) *
    i.transactionValue;

  const monthlyTotal = primaryRevenue + secondaryRevenue;
  const annualTotal = monthlyTotal * 12;
  const monthlyCost = cfg.annualCost / 12;
  const roiMultiple = cfg.annualCost > 0 ? annualTotal / cfg.annualCost : 0;

  const monthsToPayback = monthlyTotal > 0 ? monthlyCost / monthlyTotal : Infinity;
  let paybackLabel = "Not reached";
  if (Number.isFinite(monthsToPayback) && monthsToPayback > 0) {
    const days = monthsToPayback * 30.4;
    if (days < 1) paybackLabel = "Under a day";
    else if (days < 14) paybackLabel = `${Math.ceil(days)} days`;
    else if (days < 30.4) paybackLabel = `${Math.ceil(days / 7)} weeks`;
    else paybackLabel = `${monthsToPayback.toFixed(1)} months`;
  }

  const hoursReclaimed = i.adminHours * 4.33 * 0.6;

  return {
    recoveredEnquiries,
    newCustomers,
    primaryRevenue,
    secondaryRevenue,
    monthlyTotal,
    annualTotal,
    monthlyLoss: monthlyTotal,
    roiMultiple,
    paybackLabel,
    hoursReclaimed,
    monthlyCost,
  };
}
